import { Injectable } from '@angular/core';
import { fabric } from 'fabric';
import { firstValueFrom } from 'rxjs';
import { DcoLibraryService } from 'src/app/core/services/dco/dco-library.service';

export interface FontDescriptor {
  family: string;
  weight: string;
  style: 'normal' | 'italic';
  url?: string;
}

const WEIGHT_MAP: Record<string, string> = {
  thin: '100', hairline: '100',
  extralight: '200', ultralight: '200',
  light: '300',
  regular: '400', normal: '400', book: '400',
  medium: '500',
  semibold: '600', demibold: '600',
  bold: '700',
  extrabold: '800', ultrabold: '800',
  black: '900', heavy: '900',
};

const WEIGHT_KEYS = Object.keys(WEIGHT_MAP).sort((a, b) => b.length - a.length);

@Injectable({ providedIn: 'root' })
export class FontLoaderService {
  private listRequests = new Map<string, Promise<any[]>>();
  private loaded = new Map<string, Promise<void>>();

  constructor(private dcoLibraryService: DcoLibraryService) {}

  parseFontName(raw: string): FontDescriptor {
    const base = (raw || '').replace(/\.(ttf|otf|woff2?)$/i, '');
    const compact = base.toLowerCase().replace(/[\s_-]/g, '');

    const style: 'normal' | 'italic' = /italic|oblique/.test(compact) ? 'italic' : 'normal';

    let weight = '400';
    const named = WEIGHT_KEYS.find((k) => compact.includes(k));
    if (named) weight = WEIGHT_MAP[named];
    const numeric = base.match(/(?:^|[_\-\s])([1-9]00)(?![0-9]|pt|px)/i);
    if (numeric) weight = numeric[1];

    const family = base
      .replace(/[_\-\s]?\d+(?:pt|px)/gi, '')
      .replace(/[_\-\s]?(italic|oblique)/gi, '')
      .replace(new RegExp(`[_\\-\\s]?(${WEIGHT_KEYS.join('|')})`, 'gi'), '')
      .replace(/[_\-\s]?[1-9]00\b/g, '')
      .replace(/[_-]+/g, ' ')
      .trim();

    return { family: family || base, weight, style };
  }

  normalizeWeight(w: any): string {
    if (w === undefined || w === null || w === '' || w === 'normal') return '400';
    if (w === 'bold') return '700';
    return String(w);
  }

  getFontAssets(globalParams: any): Promise<any[]> {
    const params = { ...(globalParams || {}), asset_type: 'font' };
    const key = JSON.stringify(params);
    if (!this.listRequests.has(key)) {
      const req = firstValueFrom(this.dcoLibraryService.list(params))
        .then((res: any) => (res?.data ?? []).filter((a: any) => a.asset_type === 'font'))
        .catch(() => {
          this.listRequests.delete(key);
          return [];
        });
      this.listRequests.set(key, req);
    }
    return this.listRequests.get(key)!;
  }

  toDescriptor(asset: any): FontDescriptor {
    return { ...this.parseFontName(asset.name || asset.asset_name), url: asset.asset_url };
  }

  loadFont(desc: FontDescriptor): Promise<void> {
    if (!desc?.url || !desc.family) return Promise.resolve();
    const key = `${desc.family.toLowerCase()}|${desc.weight}|${desc.style}`;
    if (!this.loaded.has(key)) {
      const face = new FontFace(desc.family, `url("${desc.url}")`, {
        weight: desc.weight,
        style: desc.style,
      });
      const p = face
        .load()
        .then((f) => {
          (document.fonts as unknown as { add(f: FontFace): void }).add(f);
          fabric.util.clearFabricFontCache(desc.family);
        })
        .catch((err) => {
          this.loaded.delete(key);
          console.warn('[fonts] failed to load', desc, err);
        });
      this.loaded.set(key, p);
    }
    return this.loaded.get(key)!;
  }

  findMatch(descs: FontDescriptor[], family: string, weight: string, style: string): FontDescriptor | undefined {
    const sameFamily = descs.filter((d) => d.family.toLowerCase() === (family || '').toLowerCase());
    if (!sameFamily.length) return undefined;
    const sameStyle = sameFamily.filter((d) => d.style === style);
    const pool = sameStyle.length ? sameStyle : sameFamily;
    return pool.reduce((best, d) =>
      Math.abs(+d.weight - +weight) < Math.abs(+best.weight - +weight) ? d : best
    );
  }

  async ensureFontsForMeta(meta: any, globalParams: any): Promise<void> {
    const texts = this.collectTextObjects(meta?.objects ?? []);
    if (!texts.length) return;

    const needsLookup = texts.some((o) => !o.font_url);
    const descs = needsLookup
      ? (await this.getFontAssets(globalParams)).map((a) => this.toDescriptor(a))
      : [];

    await Promise.all(
      texts.map((o) => {
        const weight = this.normalizeWeight(o.fontWeight);
        const style = o.fontStyle === 'italic' || o.fontStyle === 'oblique' ? 'italic' : 'normal';
        if (o.font_url) {
          return this.loadFont({ family: o.fontFamily, weight, style, url: o.font_url });
        }
        const match = this.findMatch(descs, o.fontFamily, weight, style);
        return match ? this.loadFont({ ...match, family: o.fontFamily, weight, style }) : Promise.resolve();
      })
    );
  }

  private collectTextObjects(objects: any[]): any[] {
    return objects.flatMap((o) => {
      if (o?.type === 'group' && Array.isArray(o.objects)) return this.collectTextObjects(o.objects);
      return ['textbox', 'text', 'i-text'].includes(o?.type) ? [o] : [];
    });
  }
}